import discountImage from '@assets/images/delivery-box.svg'
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import Countdown from '@components/common/countdown'
import HighlightedBar from '@components/common/highlighted-bar'
import ClipboardCopy from '@components/icons/clipboard-copy'
import Carousel from '@components/ui/carousel/carousel'
import NewsTicker from '@components/ui/news-ticker'
import { useStore } from '@contexts/store'
import { useGetShopDiscounts } from '@framework/discounts/get-shop-discounts'
import { Sanitized, isHTMLStringEmpty } from '@helper/globalHelpers'
import { convertPrice } from '@hooks/use-price'
import useSessionStorage from '@hooks/use-session-storage'
import { DisplayUploadCareAsset } from '@hooks/useUploadCare'
import { ROUTES } from '@utils/routes'
import dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { toast } from 'react-toastify'
import { SwiperSlide } from 'swiper/react'
import { useModalAction } from './modal/modal.context'
import { FEATURE_KEYS } from '@utils/feature-keys'
import { useAccessControl } from '@framework/use-shop-features'

const highlightState = {
  active: false,
  expiry: null,
} as { active: boolean; expiry: string | number | null }

const OfferComponent = ({
  offer,
  getOfferPrice,
}: {
  offer: any
  getOfferPrice: (price: number) => any
}) => {
  const { analytics } = useStore()

  const copyDiscountCode = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text).then(
        function () {
          toast.success('Copied discount code')
          analytics.track('Discount code copied', { code: text })
        },
        function () {
          toast.error('Not copied successfully')
        },
      )
    },
    [analytics],
  )

  return (
    <Flex
      justify="center"
      direction={{ base: 'column', md: 'row' }}
      alignItems={'center'}
      textAlign="center"
      mt="5"
      mx="3"
      mb="6"
      gap="2.5"
      w="full"
    >
      <Box
        className="flex items-center justify-center mb-3 relative"
        minWidth="318px"
        width="318px"
        height="300px"
      >
        {offer?.url ? (
          <DisplayUploadCareAsset
            src={offer?.url}
            type={offer?.mediaType}
            alt="Discount image"
          />
        ) : (
          <Image src={'/assets/discount-image.png'} fill alt="discount" />
        )}
      </Box>
      <Flex direction="column" align="center" grow="1">
        <Text size="md" mb={2} fontSize="20px">
          GET{' '}
          <Text as="span" fontWeight="600">
            {offer?.code_type == 'percentage'
              ? `${offer?.amount}%`
              : `${getOfferPrice(offer?.amount)}`}{' '}
          </Text>
          OFF!
        </Text>
        <Box
          className="break-words"
          dangerouslySetInnerHTML={{
            __html: offer?.description,
          }}
        />

        {offer?.validfor ? (
          <Text mt="4">
            Valid till{' '}
            <Text as="span" fontWeight="bold">
              {dayjs(offer?.validfor).format('DD MMM YYYY')}
            </Text>
          </Text>
        ) : null}

        <Text mt="5" fontWeight={'600'}>
          Promo code
        </Text>

        <Flex
          height="min-content"
          mt="2"
          w="full"
          cursor="pointer"
          onClick={() => copyDiscountCode(offer?.code)}
        >
          <Flex
            justify="center"
            align="center"
            flexGrow="1"
            textAlign="center"
            border="1px solid"
            borderColor="brand"
            overflowWrap="break-word"
            wordBreak="break-word"
            py="10px"
          >
            <Text
              fontSize={{ base: '32px', lg: '40px' }}
              textTransform="uppercase"
              lineHeight="0.8"
              fontWeight="bold"
              mx="2"
            >
              {offer?.code}
            </Text>
          </Flex>
          <Button
            borderRadius="none"
            colorScheme="primary"
            leftIcon={<ClipboardCopy color="white" />}
            h="auto"
            px="2"
            minW="min-content"
            alignSelf="stretch"
          >
            Copy
          </Button>
        </Flex>
      </Flex>
    </Flex>
  )
}

const OfferBar: React.FC = () => {
  const router = useRouter()
  const { pathname } = router

  const [offerData, setOfferData] = useState<any>(null)
  const {
    analytics,
    storeData: shop,
    activeCurrency,
    isCustomDomainExpired,
  } = useStore()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { openModal } = useModalAction()
  const { hasFeature } = useAccessControl(shop?._id)

  const canShowPoliciesOnHomePage =
    shop?.config?.storefront?.policies?.find((option: any) => option?.value)
      ?.label === 'storefront'

  const isHomePage = pathname === ROUTES.HOME

  const [autoOpenPopup, setAutoOpenPopup] = useSessionStorage('autoOpenPopup', {
    state: false,
    expiry: 0,
  })

  const [canShowPolicies, setCanShowPolicies] = useSessionStorage(
    'canShowPoliciesOnHomePage',
    true,
  )

  const openOfferModal = (offer: any) => {
    analytics.track('Discount offer clicked', { ...offer })
    setOfferData(offer)
    onOpen()
  }

  const confirmPolicies = useCallback(() => {
    if (
      isHomePage &&
      canShowPoliciesOnHomePage &&
      canShowPolicies &&
      !isCustomDomainExpired
    ) {
      openModal(
        'ALL_POLICIES',
        {
          showContinueButton: true,
          onContinueClick: setCanShowPolicies(false),
        },
        {
          isCentered: true,
          hideCloseButton: true,
          closeOnOverlayClick: false,
        },
      )
    }
  }, [
    canShowPoliciesOnHomePage,
    isHomePage,
    openModal,
    canShowPolicies,
    setCanShowPolicies,
    isCustomDomainExpired,
  ])

  const closeOfferModal = (stopAutoPopup: boolean) => {
    setOfferData(null)
    onClose()
    if (autoOpenPopup?.state) {
      if (stopAutoPopup) {
        setAutoOpenPopup({
          state: false,
          expiry: new Date().getTime() + 15 * 60 * 1000,
        })
      } else {
        setAutoOpenPopup((prev: typeof autoOpenPopup) => ({
          ...prev,
          state: false,
        }))
      }
      confirmPolicies()
    }
  }

  const [highlightedBar, setHighlightedBar] = useSessionStorage(
    'highlightbar',
    highlightState,
  )

  const { results, isLoading } = useGetShopDiscounts(shop?._id)

  const offer = useMemo(
    () => ({
      description: 'Like this store? start your own online store today!',
      validfor: '2023-12-31T00:00:00.000Z',
      url: `https://qshop.tech/store/register?ref=${shop?.slug}`,
    }),
    [shop?.slug],
  )

  const offerPrice = useCallback(
    (price: number) => {
      const { price: newPrice } = convertPrice({
        amount: price,
        currency: activeCurrency,
      })
      return newPrice
    },
    [activeCurrency],
  )

  const shopOffers = useMemo(() => {
    if (isLoading || isEmpty(results)) return []
    const now = new Date().getTime()

    if (shop?.plan == 'free') {
      return []
    }

    return (
      results
        ?.filter((discount: any) => {
          const expiry = new Date(discount?.expires).getTime()
          return expiry === 0 || now < expiry
        })
        ?.map((discount: any) => ({
          ...discount,
          validfor: discount.expires,
          title: `GET ${
            discount?.code_type == 'percentage'
              ? `${discount?.amount}% OFF`
              : `${offerPrice(discount?.amount)} OFF`
          }`,
        })) ?? []
    )
  }, [isLoading, results, shop?.plan, offerPrice])

  useEffect(() => {
    if (!isLoading && isEmpty(shopOffers)) {
      confirmPolicies()
    }
  }, [isLoading, shopOffers, confirmPolicies])

  useEffect(() => {
    if (!isEmpty(shopOffers) && new Date().getTime() > autoOpenPopup?.expiry) {
      setAutoOpenPopup((prev: typeof autoOpenPopup) => ({
        ...prev,
        state: true,
      }))
    }
  }, [autoOpenPopup?.expiry, setAutoOpenPopup, shopOffers])

  useEffect(() => {
    const handledHighlightedBar = () => {
      const now = new Date().getTime()
      const expiry = new Date(highlightedBar.expiry).getTime()
      if (now > expiry) {
        setHighlightedBar({ ...highlightState, active: true, expiry: null })
      }
    }

    if (
      hasFeature(FEATURE_KEYS.ADVANCED_COUPON_OPTIONS) &&
      !isLoading &&
      !shopOffers?.length
    ) {
      setHighlightedBar({ ...highlightState, active: false })
    } else {
      handledHighlightedBar()
    }
  }, [
    shop,
    isLoading,
    shopOffers,
    highlightedBar.expiry,
    setHighlightedBar,
    hasFeature,
  ])

  const closeHighlightedBar = () => {
    // after 24hrs, the highlighted bar will be shown again
    setHighlightedBar({
      ...highlightState,
      active: false,
      expiry: new Date().getTime() + 24 * 60 * 60 * 1000,
    })
  }

  const showPopup =
    isOpen ||
    (autoOpenPopup?.state &&
      !isEmpty(shopOffers) &&
      isHomePage &&
      shop?.plan != 'free' &&
      !isCustomDomainExpired)

  return (
    <>
      {highlightedBar?.active ? (
        <HighlightedBar
          bg={shop?.plan == 'free' ? '#ff9900' : 'primary.300'}
          color={shop?.plan == 'free' ? 'white' : 'primary.100'}
          onClose={closeHighlightedBar}
        >
          {shop?.plan == 'free' ? (
            <Link href={offer.url ?? '/#'}>
              <Box className="flex items-center">
                <Box
                  mr={3}
                  className="hidden sm:flex flex-shrink-0 items-center justify-center bg-skin-fill w-9 h-9 rounded-full"
                >
                  <Image
                    width={23}
                    height={23}
                    src={discountImage}
                    alt="Delivery Box"
                  />
                </Box>
                <span
                  dangerouslySetInnerHTML={{
                    __html: offer.description,
                  }}
                />
              </Box>
            </Link>
          ) : (
            <>
              {shopOffers?.length > 0 ? (
                <NewsTicker speed={25000} delay={30000}>
                  {shopOffers?.map((offer: any, index: number) => (
                    <Fragment key={index}>
                      <Flex
                        gap={3}
                        className="items-center"
                        onClick={() => openOfferModal(offer)}
                      >
                        <div className="hidden sm:flex flex-shrink-0 items-center justify-center bg-skin-fill w-9 h-9 rounded-full me-2.5">
                          <Image
                            width={23}
                            height={23}
                            src={discountImage}
                            alt="Delivery Box"
                          />
                        </div>
                        {isHTMLStringEmpty(offer?.description) ? (
                          offer?.title
                        ) : (
                          <Sanitized html={offer?.description} />
                        )}

                        {/* <Box mx={1}>
                          | Code: <b>{offer?.code}</b> |
                        </Box> */}
                        {offer?.validfor && (
                          <Countdown date={offer?.validfor} />
                        )}
                      </Flex>
                    </Fragment>
                  ))}
                </NewsTicker>
              ) : null}
            </>
          )}
        </HighlightedBar>
      ) : null}
      {/* End of highlighted bar  */}
      <Modal
        isOpen={showPopup}
        onClose={() => closeOfferModal(false)}
        isCentered
      >
        <ModalOverlay />
        <ModalContent
          mb={0}
          mt={0}
          pb="0"
          overflow="hidden"
          position={'relative'}
          mx="2.5"
          my="4"
          maxW="700px"
        >
          <ModalCloseButton
            zIndex="popover"
            onClick={() => closeOfferModal(true)}
          />
          <ModalBody pb="0" px="0">
            <Carousel
              slidesPerView={1}
              initialSlide={
                shopOffers?.findIndex(
                  (offer: any) => offerData?._id === offer?._id,
                ) ?? 0
              }
              pagination={{
                clickable: true,
              }}
              prevButtonStyleProps={{
                'aria-label': 'Previous',
                minW: 'auto',
                h: '30px',
                w: '30px',
              }}
              nextButtonStyleProps={{
                'aria-label': 'Previous',
                minW: 'auto',
                h: '30px',
                w: '30px',
              }}
              prevActivateId="prevActivateId"
              nextActivateId="nextActivateId"
              className="discount-carousel"
            >
              {shopOffers?.map((offer: any, id: number) => (
                <SwiperSlide
                  key={`discount-gallery-${id}`}
                  className="flex items-center justify-center"
                >
                  <OfferComponent offer={offer} getOfferPrice={offerPrice} />
                </SwiperSlide>
              ))}
            </Carousel>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}

export default OfferBar

OfferBar.displayName = 'Offers'
